import { HttpErrorResponse } from '@angular/common/http';
import { LimiteLegge } from './controlli.model';

// per gli errori 4xx il backend restituisce un messaggio pensato per l'utente (es. 422 "nessun dato
// classificabile"); per gli altri (5xx, rete) si mostra un testo generico legato alla chiamata
export function messaggioErrore(err: unknown, testoGenerico: string): string {
  if (err instanceof HttpErrorResponse && err.status >= 400 && err.status < 500 && typeof err.error?.message === 'string') {
    return err.error.message;
  }
  return testoGenerico;
}

export function isNessunDatoClassificabile(err: unknown): boolean {
  return err instanceof HttpErrorResponse && err.status === 422;
}

export type IndiceRischio = 'last' | 'q95' | 'integrated';

// Kode calcola la classe di rischio solo per parametri quantitativi con limite massimo di legge
// ("< x" o un valore singolo, es. 0 per i batteri coliformi): esclusi i qualitativi, i limiti
// minimi (">= x"), gli intervalli (pH) e i parametri senza limite ("*")
export function isParametroClassificabile(limite: LimiteLegge): boolean {
  const range = (limite.range_consentito ?? '').trim();
  if (limite.tipo_valore === 'boolean') return false;
  if (!range || range === '*') return false;
  if (range.startsWith('>')) return false;
  return !/\se\s/.test(range);
}

export type RiskClass = '< LOQ' | 'R1' | 'R2' | 'R3' | 'R4' | 'R5';

export type CriticalityClass = 'non disponibile' | 'Trascurabile' | 'Lieve' | 'Moderata' | 'Significativa' | 'Grave' | 'Critica';

export const RISK_CLASSES: RiskClass[] = ['< LOQ', 'R1', 'R2', 'R3', 'R4', 'R5'];

export const CRITICALITY_CLASSES: CriticalityClass[] = ['non disponibile', 'Trascurabile', 'Lieve', 'Moderata', 'Significativa', 'Grave', 'Critica'];

export interface FiltriRischioAnalitico {
  istatComune?: number[];
  istatRegione?: number[];
  tipoPuntoCampionamento?: string[];
  poligono?: object;
  codiciParametro?: string[];
  dataInizio?: string;
  dataFine?: string;
}

export interface PuntiRischioRequest extends FiltriRischioAnalitico {
  index: IndiceRischio;
  annoRiferimento?: number;
}

export interface FiltriComuniRequest extends FiltriRischioAnalitico {
  annoRiferimento?: number;
}

// stato editabile dal form filtri (poligono escluso: viene impostato dal disegno sulla mappa, non dal form)
export interface RischioAnaliticoFiltriValue {
  istatComune: number[];
  tipoPuntoCampionamento: string[];
  codiciParametro: string[];
  criterio: IndiceRischio;
  dataInizio: string | null;
  dataFine: string | null;
  annoRiferimento: number;
}

export function filtriDefault(): RischioAnaliticoFiltriValue {
  return {
    istatComune: [],
    tipoPuntoCampionamento: [],
    codiciParametro: [],
    criterio: 'last',
    dataInizio: null,
    dataFine: null,
    annoRiferimento: new Date().getFullYear()
  };
}

export function richiestaPunti(filtri: RischioAnaliticoFiltriValue, poligono: object | null): PuntiRischioRequest {
  const base = richiestaBase(filtri, poligono);
  if (filtri.criterio === 'last') {
    return { ...base, index: 'last', dataInizio: filtri.dataInizio ?? undefined, dataFine: filtri.dataFine ?? undefined };
  }
  return { ...base, index: filtri.criterio, annoRiferimento: filtri.annoRiferimento };
}

export function richiestaFiltriComuni(filtri: RischioAnaliticoFiltriValue, poligono: object | null): FiltriComuniRequest {
  return { ...richiestaBase(filtri, poligono), annoRiferimento: filtri.annoRiferimento };
}

function richiestaBase(filtri: RischioAnaliticoFiltriValue, poligono: object | null): FiltriRischioAnalitico {
  return {
    istatComune: filtri.istatComune?.length ? filtri.istatComune : undefined,
    tipoPuntoCampionamento: filtri.tipoPuntoCampionamento?.length ? filtri.tipoPuntoCampionamento : undefined,
    codiciParametro: filtri.codiciParametro?.length ? filtri.codiciParametro : undefined,
    poligono: poligono ?? undefined
  };
}

export interface PuntoRischioResponse {
  id: number;
  puntoUuid: string;
  idPuntoCampionamento: string;
  istatComune: number;
  nomeComune: string | null;
  istatRegione: number;
  tipoPuntoCampionamento: string;
  lat: number;
  lon: number;
  index: IndiceRischio;
  classeRischio: string | null;
  numeroOsservazioni: number;
}

// avviso valorizzato quando nessun analita selezionato e' classificabile: punti tutti con classeRischio null
export interface PuntiRischioResponse {
  punti: PuntoRischioResponse[];
  avviso: string | null;
}

export interface Q95ClassCountRow {
  sampling_point_id: number;
  punto: string;
  risk_class: RiskClass;
  n: number;
}

// una cella della heatmap punto x parametro (asse X = punto, asse Y = parametro, colore = risk_class)
export interface HeatmapCella {
  sampling_point_id: number;
  punto: string;
  parameter: string;
  um: string;
  n_obs: number;
  risk_class: RiskClass;
}

export interface SerieStoricaRow {
  sampling_point_id: number;
  parameter: string;
  um: string;
  date: string;
  value: number;
  risk_class: RiskClass | null;
}

export interface DistribuzioneStatistica {
  numeroOsservazioni: number;
  min: number;
  max: number;
  media: number;
  mediana: number;
  percentile25: number;
  percentile75: number;
  percentile95: number;
  deviazioneStandard: number;
  istogramma: { binMin: number; binMax: number; count: number }[];
}

export interface ParametroDettaglio {
  codiceParametro: string;
  unitaMisura: string | null;
  obbligatorio: boolean;
  rangeConsentito: string | null;
  tipoValore: string;
  distribuzione: DistribuzioneStatistica | null;
}

export interface PuntoDettaglioResponse {
  puntoUuid: string;
  serieStorica: SerieStoricaRow[];
  parametri: ParametroDettaglio[];
}

export interface OpzioneComune {
  codice: number;
  nome: string | null;
}

export interface TipoPuntoCampionamentoLookup {
  codice: string;
  categoria: string;
  descrizione: string;
}

// nomi estesi delle 6 macro-categorie AnTeA
export const NOMI_CATEGORIA_TIPO_PUNTO: Record<string, string> = {
  A: 'Captazioni',
  B: 'Trattamenti',
  C: 'Adduzione',
  D: 'Accumulo',
  E: 'Distribuzione',
  F: 'Punti d\'uso'
};

export function nomeCategoriaTipoPunto(categoria: string): string {
  return NOMI_CATEGORIA_TIPO_PUNTO[categoria] ?? categoria;
}

export const COLORI_RISK_CLASS: Record<RiskClass, string> = {
  '< LOQ': '#9e9e9e',
  R1: '#1976d2',
  R2: '#4fc3f7',
  R3: '#43a047',
  R4: '#fb8c00',
  R5: '#e53935'
};

export const COLORI_CRITICALITY: Record<CriticalityClass, string> = {
  'non disponibile': '#ffffff',
  Trascurabile: '#9e9e9e',
  Lieve: '#1976d2',
  Moderata: '#4fc3f7',
  Significativa: '#43a047',
  Grave: '#fb8c00',
  Critica: '#e53935'
};

// classeRischio === null: nessuna classificazione restituita da Kode per il punto (stato diverso
// da "non disponibile", che e' un valore di business dell'indice integrato) — grigio scuro per
// restare distinguibile sia da "< LOQ"/"Trascurabile" (grigio chiaro) sia da "non disponibile" (bianco)
export const COLORE_CLASSE_ASSENTE = '#616161';

export function classiPerCriterio(criterio: IndiceRischio): string[] {
  return criterio === 'integrated' ? CRITICALITY_CLASSES : RISK_CLASSES;
}

// id univoco dello stile OpenLayers registrato per una classe (LayerStylesService.mapStyles)
export function sluggificaClasse(classe: string | null): string {
  if (classe === null) return 'assente';
  return classe
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '');
}

export function idStileClasse(classe: string | null): string {
  return `rischio_analitico_${sluggificaClasse(classe)}`;
}

export function hexToRgb(hex: string): { r: number; g: number; b: number } {
  const pulito = hex.replace('#', '');
  return {
    r: parseInt(pulito.substring(0, 2), 16),
    g: parseInt(pulito.substring(2, 4), 16),
    b: parseInt(pulito.substring(4, 6), 16)
  };
}

export function coloreClasse(classe: string | null, criterio: IndiceRischio): string {
  if (classe === null) return COLORE_CLASSE_ASSENTE;
  if (criterio === 'integrated') return COLORI_CRITICALITY[classe as CriticalityClass] ?? COLORE_CLASSE_ASSENTE;
  return COLORI_RISK_CLASS[classe as RiskClass] ?? COLORE_CLASSE_ASSENTE;
}

export function finestraTemporale(
  criterio: IndiceRischio,
  annoRiferimento: number,
  dataInizio?: string,
  dataFine?: string
): { dataInizio?: string; dataFine?: string } {
  if (criterio === 'last') return { dataInizio, dataFine };
  const annoEffettivo = criterio === 'integrated' ? new Date().getFullYear() : annoRiferimento;
  return { dataInizio: `${annoEffettivo - 3}-01-01`, dataFine: `${annoEffettivo - 1}-12-31` };
}

// range_consentito e' testo libero in limiti_legge (es. "< 10", "< 0,50", "0"): estrae il limite
// numerico per la reference line del trend, null se non e' un limite superiore disegnabile
export function limiteLeggeNumerico(rangeConsentito: string | null | undefined): number | null {
  if (!rangeConsentito) return null;
  const pulito = rangeConsentito
    .replace(/[<>≤≥=]/g, '')
    .replace(',', '.')
    .trim();
  const valore = Number(pulito);
  if (!Number.isFinite(valore) || valore <= 0) return null;
  return valore;
}
